import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authParams, getBase64Hash } from 'components/helpers/base64AuthParams';
import { userObj } from 'components/common/requestBody';
import getLanguage from 'components/helpers/getLanguage';
import translationKeys from 'translations/translationKeys';
import axios from 'axios';
import defaultSportId from 'components/helpers/defaultSport';
import baseUrls from '../../config';

const apiBaseUrl = baseUrls.BASE_URL_API;
const apiVersion2 = baseUrls.API_VERSION2;
const client = axios.create({
  baseURL: apiBaseUrl,
});

const { msg29 } = translationKeys;

export const getAllSports = createAsyncThunk(
  'sports/getAllSports',
  async (thunkAPI) => {
    const lang = getLanguage();
    const api = `/sport/0/sports?channel_id=${userObj.chl}&lang=${lang}`;
    const signatureParam = `/sport/0/sportschannel_id=${userObj.chl}&lang=${lang}`;
    const url = `/${apiVersion2}${api}`;
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          'Content-Signature': `${authParams.username}:${getBase64Hash(`${authParams.username}/${apiVersion2}${signatureParam}`)}`,
        },
      };

      if (baseUrls.PARTNER_SLUG) {
        config.headers['X-Partner-Slug'] = baseUrls.PARTNER_SLUG;
      }
      const resp = await client.get(url, config);
      return resp.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(msg29);
    }
  },
);

// Each context has its own localStorage key
const loadSport = (key) => {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : { id: defaultSportId };
  } catch {
    return { id: defaultSportId };
  }
};

const initialState = {
  allSports: [],
  isLoading: true,
  error: '',
  menuSportSelected: loadSport('menuSportSelected'),
  todaySportSelected: loadSport('todaySportSelected'),
  topSportSelected: loadSport('topSportSelected'),
};

const allSportsSlice = createSlice({
  name: 'allSports',
  initialState,
  reducers: {
    // Legacy — kept for LeftSideBar menu navigation (selects sport in sidebar)
    selectSport: (state, { payload }) => {
      let found = { id: payload };
      const updatedSports = state.allSports.map((sport) => {
        if (sport.id === payload) {
          found = sport;
          localStorage.setItem('menuSportSelected', JSON.stringify(sport));
          return { ...sport, selected: true };
        }
        return sport;
      });
      return { ...state, allSports: updatedSports, menuSportSelected: found };
    },

    selectMenuSport: (state, { payload }) => {
      const found = state.allSports.find((s) => s.id === payload) || { id: payload };
      localStorage.setItem('menuSportSelected', JSON.stringify(found));
      return { ...state, menuSportSelected: found };
    },

    selectTodaySport: (state, { payload }) => {
      const found = state.allSports.find((s) => s.id === payload) || { id: payload };
      localStorage.setItem('todaySportSelected', JSON.stringify(found));
      return { ...state, todaySportSelected: found };
    },

    selectTopSport: (state, { payload }) => {
      const found = state.allSports.find((s) => s.id === payload) || { id: payload };
      localStorage.setItem('topSportSelected', JSON.stringify(found));
      return { ...state, topSportSelected: found };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getAllSports.pending, (state) => ({ ...state, isLoading: true }))
      .addCase(getAllSports.fulfilled, (state, action) => {
        const allSportsWithPath = action.payload.map((item) => ({
          ...item,
          path: item.name.toLowerCase().replace(/\s/g, ''),
          selected: false,
        }));

        const defaultSport = action.payload.find((s) => s.id === defaultSportId)
        || { id: defaultSportId };

        // Only seed from API if nothing was previously saved
        const menuSportSelected = loadSport('menuSportSelected')?.id !== defaultSportId
          ? loadSport('menuSportSelected') : defaultSport;
        const todaySportSelected = loadSport('todaySportSelected')?.id !== defaultSportId
          ? loadSport('todaySportSelected') : defaultSport;
        const topSportSelected = loadSport('topSportSelected')?.id !== defaultSportId
          ? loadSport('topSportSelected') : defaultSport;

        return {
          ...state,
          allSports: allSportsWithPath,
          isLoading: false,
          menuSportSelected,
          todaySportSelected,
          topSportSelected,
        };
      })
      .addCase(getAllSports.rejected, (state) => ({
        ...state,
        isLoading: false,
        error: msg29,
      }));
  },
});

export const {
  selectSport,
  selectMenuSport,
  selectTodaySport,
  selectTopSport,
} = allSportsSlice.actions;

export default allSportsSlice.reducer;
